import React from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useToast,
  IconButton,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Spinner,
  Center,
  Flex,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { PermissionForm } from "../components/forms/PermissionForm";
import api from "../services/api";
import { GroupedPermission } from "../types";

export const PermissionsPage: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedGroup, setSelectedGroup] =
    React.useState<GroupedPermission | null>(null);
  const [groupToDelete, setGroupToDelete] =
    React.useState<GroupedPermission | null>(null);

  // Theme colors matching DataTable
  const tableContainerBorderColor = useColorModeValue("gray.200", "gray.700");
  const tableBgColor = useColorModeValue("light.700", "dark.700");
  const tableBgColorSecondary = useColorModeValue("light.200", "dark.200");
  const accordionBgExpanded = useColorModeValue("brand.50", "whiteAlpha.100");

  const { data, isLoading, error } = useQuery({
    queryKey: ["permissions-grouped"],
    queryFn: api.permissions.getGrouped,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.permissions.delete(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ["permissions-grouped"] });
      toast({
        title: "آیتم حذف شد",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در حذف",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: api.permissions.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions-grouped"] });
      onClose();
      toast({
        title: "منبع جدید ایجاد شد",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در ایجاد",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.permissions.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions-grouped"] });
      onClose();
      setSelectedGroup(null);
      toast({
        title: "منبع با موفقیت ویرایش شد",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در ویرایش",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
      });
    },
  });

  const handleDelete = (group: GroupedPermission) => {
    setGroupToDelete(group);
    onDeleteOpen();
  };

  const handleEdit = (group: GroupedPermission) => {
    setSelectedGroup(group);
    onOpen();
  };

  const handleFormSubmit = (formData: any) => {
    if (selectedGroup) {
      updateMutation.mutate({
        id: selectedGroup.permissions[0]._id,
        data: {
          label: formData.label,
          resourceLabel: formData.label,
          description: formData.description,
        },
      });
      return;
    }

    createMutation.mutate(formData);
  };

  const confirmDelete = () => {
    if (!groupToDelete) return;
    deleteMutation.mutate(groupToDelete.permissions[0]._id, {
      onSuccess: () => {
        onDeleteClose();
        setGroupToDelete(null);
      },
    });
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Heading size="md" color="red.500">
          خطا در بارگذاری مجوزها
        </Heading>
      </Box>
    );
  }

  const groupedData = data?.data || [];

  return (
    <Flex
      flexDir={"column"}
      w="100%"
      h="100%"
      overflowY={"auto"}
      borderRadius={"2xl"}
      border="1px solid"
      borderColor={tableContainerBorderColor}
    >
      <Box
        w="100%"
        h={"100%"}
        display="flex"
        flexDirection="column"
        justifyContent={"space-between"}
      >
        <Flex flexDir={"column"} w="100%">
          {/* Header Section - Matching DataTable Style */}
          <Flex
            w="100%"
            justify="space-between"
            align="center"
            bg={tableBgColorSecondary}
            borderBottom="1px solid"
            borderColor={tableContainerBorderColor}
            px={5}
            py={4}
          >
            <Flex flexDir={"column"}>
              <Heading size="md" mb={2}>
                مدیریت مجوزها
              </Heading>
              <Text fontSize="sm">
                {groupedData.length} منبع -{" "}
                {groupedData.reduce(
                  (acc: number, g: GroupedPermission) =>
                    acc + g.permissions.length,
                  0,
                )}{" "}
                مجوز
              </Text>
            </Flex>
            <Button
              borderRadius={"full"}
              fontSize={"sm"}
              onClick={() => {
                setSelectedGroup(null);
                onOpen();
              }}
            >
              افزودن منبع جدید
            </Button>
          </Flex>

          {/* Accordion Section */}
          <Box p={6}>
            <Accordion allowMultiple>
              {groupedData.map((group: GroupedPermission) => (
                <AccordionItem
                  key={group.resource}
                  border="1px"
                  borderColor={tableContainerBorderColor}
                  mb={3}
                >
                  <AccordionButton _expanded={{ bg: accordionBgExpanded }}>
                    <Box flex="1" textAlign="right">
                      <HStack spacing={3}>
                        <Heading size="md">{group.resourceLabel}</Heading>
                        <Badge colorScheme="brand">{group.resource}</Badge>
                        <Badge>{group.permissions.length} مجوز</Badge>
                      </HStack>
                    </Box>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="ویرایش منبع"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(group);
                        }}
                      />
                      <IconButton
                        aria-label="حذف منبع"
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(group);
                        }}
                        isLoading={deleteMutation.isPending}
                      />
                      <AccordionIcon />
                    </HStack>
                  </AccordionButton>
                  <AccordionPanel p={0}>
                    <Table variant="simple" size="sm">
                      <Thead bg={tableBgColor}>
                        <Tr>
                          <Th>عنوان</Th>
                          <Th>توضیحات</Th>
                          <Th>عملیات</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {group.permissions.map((permission) => (
                          <Tr key={permission._id}>
                            <Td>{permission.label}</Td>
                            <Td>{permission.description}</Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  permission.action === "delete"
                                    ? "red"
                                    : permission.action === "create"
                                      ? "green"
                                      : permission.action === "update"
                                        ? "orange"
                                        : "blue"
                                }
                              >
                                {permission.action}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        </Flex>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedGroup ? "ویرایش منبع" : "افزودن منبع جدید"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <PermissionForm
              initialData={
                selectedGroup
                  ? {
                      resource: selectedGroup.resource,
                      label: selectedGroup.resourceLabel,
                      description: String(
                        selectedGroup.permissions[0]?.description || "",
                      )
                        .replace(/^(مشاهده|ایجاد|ویرایش|حذف)\s+/, "")
                        .trim(),
                    }
                  : undefined
              }
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setSelectedGroup(null);
                onClose();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>حذف منبع</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Text>
                آیا از حذف تمام مجوزهای "{groupToDelete?.resourceLabel || "-"}"
                اطمینان دارید؟
              </Text>
              <HStack spacing={3}>
                <Button
                  flex={1}
                  variant="outline"
                  onClick={() => {
                    setGroupToDelete(null);
                    onDeleteClose();
                  }}
                >
                  انصراف
                </Button>
                <Button
                  flex={1}
                  colorScheme="red"
                  onClick={confirmDelete}
                  isLoading={deleteMutation.isPending}
                >
                  حذف
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};
